import React from 'react';
import { useSelector } from 'react-redux';
import GenericCard from '../GenericCard';
import { HOST, protocolCompatible } from './protocol';
import { FACE_KIND } from './faceKind';
import { handleCardMessage, isFromCard, makeActionGate, makeFaceGate } from './handleCardMessage';
import { makeCardStore } from './cardStore';
import { buildCardDocument } from './buildCardDocument';
import { entryNeedsChannel } from './cardIndex';
import { carriedContinue } from '../continueAt';
import { wsUrl } from '../../store/wsUrl';

// card-sandbox: host component for a Tier-2 (custom-code, third-party) card.
//
// Renders the card inside an opaque-origin sandboxed iframe (A1) and bridges it to
// the host over the versioned postMessage protocol. Replaces the page-privileged
// <script>-injection runtime path for pulled cards.
//
// The trust-critical logic lives in the tested `handleCardMessage` (action gate +
// host storage + source guard); this component is the DOM/iframe wiring, which
// jsdom can't exercise realistically and gets live verification instead.
//
// v2 messaging (ADR 2026-07-24-card-sandbox-messaging, fork A): a card:invoke is a
// host-mediated backend call. The host holds a DEDICATED qlink socket (the proven
// SimulatorViewer pattern) — NOT the chat session. The chat `/s/assist` socket
// routes ACTIONs through the agent and returns a plain MAIL (ref dropped), so a
// correlated request/response is impossible there; a dedicated socket gets the
// qlink `PAYLOAD_MESSAGE(ref)` reply straight through. The card still has NO
// network and never sees the socket — the host owns it, the gate, and the identity
// stamp. A v1 (self-contained) card never invokes, so the socket is never opened.
//
// Props:
//   structured  — the emit_card payload
//   onAction    — CardFor's action dispatcher (fire-and-forget, surfaces in chat)
//   entry       — { app_id, url, function_sets?, action_url? } from /apps/cards.json.
//                 action_url (standalone-card-action) routes an invoke DIRECT to the
//                 app's own worker over HTTP (agentless), bypassing the WS-to-agent path.
//   accountId   — logged-in account, for (account,kind)-scoped storage
//   liveEventId — the live event this card is (live_events): its invokes name it, and only
//                 pushes for it reach the card
//   hidden      — a live card kept running out of sight; it gets lifecycle 'hide'/'show'
//   onEnd       — the card says it has finished; onActivity — it invoked its helper

// science-sim-orchestrator Tier 3: kinds whose code is delivered INLINE (in the
// structured payload) rather than as a compiled bundle. Kept to an explicit allowlist
// so a wire `__artifact_code__` field can only trigger the inline-code path for a card
// deliberately designated as an artifact — everything else fetches its bundle.
export const ARTIFACT_KINDS = new Set(['science.artifact', 'station.face']);

// Model-written cards (a face, a science artifact) never fire a Continue themselves: their code
// isn't the helper author's, and a Continue navigates the viewer away. A page that shows such a
// card draws its Continue outside the sandbox.
export function sandboxContinue(kind, structured) {
  if (ARTIFACT_KINDS.has(kind)) return () => null;
  return (action) => carriedContinue(structured, action);
}

// A card a helper emitted: an object, or (the usual wire shape) its JSON text.
function asCard(c) {
  if (typeof c === 'string') {
    try { c = JSON.parse(c); } catch (e) { return null; }
  }
  return c && typeof c === 'object' && !Array.isArray(c) ? c : null;
}

// A face's call result: the station's reply text and the cards it emitted.
export function faceValue(resultRow, cardsRow, textRow) {
  let cards = [];
  try {
    const parsed = cardsRow ? JSON.parse(cardsRow.key) : [];
    if (Array.isArray(parsed)) cards = parsed.map(asCard).filter(Boolean);
  } catch (e) { /* not JSON: no cards */ }
  const row = resultRow || textRow;
  return { text: row && row.key != null ? String(row.key) : '', cards };
}

// station-face: a face reads its station's functions, so its calls take the tile rail, whose
// reply comes back to this card (in chat a reply would go to the chat instead).
export { FACE_KIND };

// The station comes from the face the server emitted, never from what the card passes to invoke.
export function faceInvokeParams(action, faceStructured, wireRef) {
  const sid = faceStructured && faceStructured.station_id;
  if (sid == null || sid === '') return null;
  return { ...invokeParams(action, sid, null, wireRef), inline: '1', face: '1' };
}

// A card may invoke from onInit, before its socket has a session: wait for it, briefly.
export function whenReady(isReady, timeoutMs = 5000, stepMs = 100) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (isReady()) resolve(true);
      else if (Date.now() - start >= timeoutMs) resolve(false);
      else setTimeout(tick, stepMs);
    };
    tick();
  });
}

// fetch seam (jsdom / tests). The parent fetches the card JS SAME-ORIGIN and inlines
// it into the srcdoc; the parent never *executes* it — the sandboxed iframe does.
//
// `cache: 'no-cache'` forces a revalidation (conditional GET via ETag) on every
// load. Card bundles are served at a STABLE url (…/cards/<kind>.js) with no
// Cache-Control, so without this the browser heuristically caches them and a
// PUBLISHED card update never reaches the page. Revalidation is cheap: unchanged
// → 304, changed → the new bundle.
let _fetchSource = (url) => fetch(url, { credentials: 'omit', cache: 'no-cache' }).then((r) => (r.ok ? r.text() : null));
export function _setSourceFetcher(fn) { _fetchSource = fn; }

// WebSocket seam (tests): swappable so a test can assert the invoke frame without a
// real socket. Defaults to the browser WebSocket.
let _makeSocket = (url) => new WebSocket(url);
export function _setSocketFactory(fn) { _makeSocket = fn; }

// standalone-card-action: the AGENTLESS transport seam. When the card's `entry`
// carries an `action_url` (the app's own standalone worker's POST /card/action —
// ADR 2026-08-04-standalone-card-action), an invoke goes DIRECT to the worker over
// HTTP instead of the WS-to-agent path, and — unlike the fire-and-forget WS relay —
// resolves the card's invoke Promise with the worker's REAL synchronous reply.
// Credential-less (anonymous; the worker server-mints the guest identity). Swappable
// for tests. Returns the parsed JSON reply ({ok, result[, error]}) or null on failure.
let _actionFetch = (url, body) => fetch(url, {
  method: 'POST',
  credentials: 'omit',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
}).then((r) => r.json()).catch(() => null);
export function _setActionFetcher(fn) { _actionFetch = fn; }

// Turn a card action_spec ({function_set, function, ...args}) into the qlink:invoke
// params. Identity (station, aveq_id) is stamped by the HOST from the chat session —
// never supplied by the card. Shared by BOTH transports (the WS-to-agent frame AND
// the standalone-card-action HTTP body), so the worker receives one consistent shape.
export function invokeParams(action, stationId, threadId, ref, liveEventId) {
  const fnSet = action.function_set || action.functionSet;
  const { function_set, functionSet, function: fn, _live_event, ...args } = action;
  return {
    ref,
    station: String(stationId),
    class: fnSet,
    function: fn,
    act_params: JSON.stringify({
      ...args, aveq_id: threadId || '',
      // The worker echoes it on everything this call emits, so the output finds this card.
      ...(threadId && liveEventId ? { _live_event: liveEventId } : {}),
    }),
    // tile-card-actions: with NO chat thread there is nowhere for the helper's reply to come
    // back to — a card push rides a chat stream, and a tile has none. That is the same
    // condition, so it is the marker: `tile` routes the invoke down ws.py's in-process rail
    // (`_dispatch_tile_invoke`), which answers on THIS socket with a ref-correlated
    // PAYLOAD_MESSAGE. Identity stays server-resolved there (viewer from the session; the
    // client's `aveq_id`/`aveq_user` are popped), and it is gated on can_access + the
    // station's card-declared function sets. A chat invoke has a thread, is not marked, and
    // keeps its existing semantics exactly.
    ...(threadId ? {} : { tile: true }),
  };
}

// How long a TILE invoke waits for the rail's ref-correlated reply before falling back to the
// refresh channel. Long enough for a helper burst to run and answer; short enough that a card
// is never left waiting on a reply that is not coming.
const TILE_REPLY_TIMEOUT_MS = 8000;

export default function SandboxedCard({ structured, onAction, entry, accountId, fillHeight, onSwipe,
                                       reduceMotion, onLoaded, onFailed, liveEventId, hidden,
                                       onEnd, onActivity }) {
  const kind = structured.kind;
  const iframeRef = React.useRef(null);
  // wireRef -> { reqId, timer } for TILE invokes awaiting the rail's PAYLOAD_MESSAGE. Only a
  // ref we are holding is ever settled, so this correlates — it does not relay.
  const pendingRef = React.useRef({});
  // Per-INSTANCE entropy for that key. `reqId` is a counter starting at 'i1' in every card
  // document, so without this two viewers of the same tile — and the same viewer's next mount —
  // all generate `cardinvoke_robot.budget:i1`. The delivery is unicast today, so a foreign
  // frame cannot arrive; but that makes the safety a property of ws.py's send, not of this
  // check, and MAIL on this very socket was once NOT unicast — which is the leak
  // card-sandbox-messaging-push closed. An unguessable ref makes the correlation carry its own
  // weight, so a future fan-out cannot quietly turn "correlated" into "relayed". (!636 #1.)
  const refSaltRef = React.useRef(null);
  if (refSaltRef.current === null) {
    refSaltRef.current = Math.random().toString(36).slice(2, 10)
      + Math.random().toString(36).slice(2, 10);
  }
  // Chat state — for the station to subscribe the dedicated socket to, and the
  // thread to stamp as identity. Held in a ref so the message bridge doesn't
  // re-subscribe on every chat-state change.
  const { station: chatStation, threadId, connectionStatus, cardPush } = useSelector((s) => s.chat);
  // station-install: a face belongs to the station in its payload — the same one as the chat's
  // where there is a chat, and the only one there is on the Stations page grid.
  const faceStationId = kind === FACE_KIND && structured && structured.station_id
    ? Number(structured.station_id) : null;
  const station = faceStationId ? { station: { station_id: faceStationId } } : chatStation;
  const sessionRef = React.useRef({ station, threadId });
  sessionRef.current = { station, threadId };
  // station-offline-pause: chat-WS liveness gates whether a card "runs". Off any chat (a face
  // on the Stations page) there is no chat socket to wait for.
  const offChat = !(chatStation && chatStation.station);
  const online = connectionStatus === 'live' || (Boolean(faceStationId) && offChat);
  const onlineRef = React.useRef(online);
  onlineRef.current = online;
  const hiddenRef = React.useRef(hidden);
  hiddenRef.current = hidden;
  // A primitive so the socket effect can depend on it: the socket opens once the
  // station is known and re-tries if the station arrives after `doc` (guards the
  // doc-resolves-first ordering — !382 review Note 1).
  const stationId = station && station.station && station.station.station_id;

  const [doc, setDoc] = React.useState(null);   // srcdoc string once fetched
  const [height, setHeight] = React.useState(120);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { if (failed && onFailed) onFailed(); }, [failed, onFailed]);

  // The dedicated qlink socket + its own session id (distinct from the chat session).
  const socketRef = React.useRef(null);
  const dedSessionRef = React.useRef(null);
  const lastRevealRef = React.useRef(0);

  // The action gate: a card may drive only its OWN helper's function-set(s), from
  // the card index. Absent → deny all (fail closed): the card renders, but can't act.
  const isFace = kind === FACE_KIND;
  const gate = React.useMemo(
    () => (isFace ? makeFaceGate(structured && structured.invokes)
      : makeActionGate((entry && entry.function_sets) || [])),
    [entry, isFace, structured],
  );
  // gallery-efficiency: does this card need a live backend channel? A 'display'
  // card renders from its payload and never invokes, so we open NO qlink socket
  // for it (was eager-opened for every card once the station was known — the
  // read-only-card-holds-a-socket waste). Author-declared `entry.mode` wins;
  // absent (mesh cards don't publish it yet) → derive from the gate's
  // function_sets, since a card that can emit nothing can't need a channel.
  // See ADR 2026-08-13-gallery-efficiency.
  const needsChannel = React.useMemo(() => entryNeedsChannel(entry), [entry]);
  const store = React.useMemo(
    () => makeCardStore(accountId != null ? accountId : 'anon', kind),
    [accountId, kind],
  );

  // Fetch + wrap the card source into a srcdoc document.
  React.useEffect(() => {
    // science-sim-orchestrator Tier 3 (artifact card): for a designated artifact
    // kind, the simulation code is delivered INLINE in the structured payload
    // (LLM-generated) rather than fetched as a compiled bundle. It runs in the SAME
    // hardened sandbox as every other card — opaque origin (no cookies/parent DOM),
    // CSP `default-src 'none'; script-src 'unsafe-inline'` (an inline <script> runs,
    // but NO eval/new Function and NO network), and the host-mediated, action-GATED
    // invoke bridge. buildCardDocument bakes it in exactly like a bundle. Trust: only
    // a REGISTERED kind reaches SandboxedCard at all, the inline path is limited to an
    // explicit artifact-kind allowlist, and the invoke gate still comes from the
    // entry's function_sets (server-declared) — the wire supplies the code, never its
    // permissions. The generated code is thus fully contained: worst case is a broken
    // animation in an isolated frame.
    const inlineCode = ARTIFACT_KINDS.has(kind) && structured && structured.__artifact_code__;
    if (typeof inlineCode === 'string' && inlineCode) {
      setDoc(buildCardDocument(inlineCode));
      return undefined;
    }
    if (!entry || !entry.url) { setFailed(true); return undefined; }
    let alive = true;
    _fetchSource(entry.url)
      .then((src) => { if (alive) (src ? setDoc(buildCardDocument(src)) : setFailed(true)); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [entry, kind, structured && structured.__artifact_code__]);

  // Open the dedicated qlink socket once the card is rendering and we know the
  // station. Its ONLY jobs now: SUBSCRIBE_STATION + capture the session id (to
  // stamp on invokes), and SEND card:invoke ACTIONs. Idempotent: a socket already
  // open/connecting isn't reopened (guards a double-mount). Closed on unmount.
  //
  // card-sandbox-messaging-push: this socket NO LONGER relays the helper's reply.
  // A card:invoke stamps act_params.aveq_id = the chat threadId, so the helper's
  // emit_card_push routes back on the CHAT socket and is delivered to the matching
  // card BY KIND via Redux (meta.cardPush → setCardPush → the relay effect below).
  // That retires the old last-subscriber MAIL relay — an OBSERVED, uncontracted
  // qbric behavior that ALSO leaked other participants' MAILs into a card on a
  // shared station (the leak this slug closes). See ADR
  // 2026-07-24-card-sandbox-messaging (Addendum) + the card-sandbox-messaging-push slug.
  React.useEffect(() => {
    // gallery-efficiency: a 'display' card opens no socket at all.
    if (!doc || !stationId || !needsChannel) return undefined;
    // Don't open a second socket if one is already live (double-mount guard).
    if (socketRef.current
        && (socketRef.current.readyState === 0 || socketRef.current.readyState === 1)) {
      return undefined;
    }

    const socket = _makeSocket(wsUrl());
    socketRef.current = socket;
    socket.onopen = () => {
      try {
        socket.send(JSON.stringify({
          messageType: 'SUBSCRIBE_STATION', stationId, timestamp: 0,
        }));
      } catch (e) { /* socket died */ }
    };
    socket.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch (e) { return; }
      if (data.messageType === 'SUBSCRIBER_METRICS') {
        dedSessionRef.current = data.sessionId;
      }
      // tile-card-actions: settle a TILE invoke with the rail's real reply. `_dispatch_tile_invoke`
      // answers on this socket with a ref-correlated PAYLOAD_MESSAGE, so the card gets the
      // helper's value in the SAME instance — which is how chat has always worked, and what a
      // tile could not do: with no thread the push had no address, leaving the tile's own
      // refresh as the state channel. That refresh REBUILDS the iframe, so every interaction
      // destroyed the card instance and everything in it.
      //
      // This is correlation, not relay: a frame is delivered only when its `ref` matches an
      // invoke THIS card is waiting on. It is not the unfiltered MAIL relay that
      // card-sandbox-messaging-push removed — nothing is accepted that we did not just ask for.
      if (data.messageType === 'PAYLOAD_MESSAGE' && data.ref && pendingRef.current[data.ref]) {
        const { reqId, timer } = pendingRef.current[data.ref];
        if (timer) clearTimeout(timer);
        delete pendingRef.current[data.ref];
        const rows = Array.isArray(data.payload) ? data.payload : [];
        const err = rows.find((r) => r && r.name === 'error');
        const res = rows.find((r) => r && r.name === 'result');
        const value = isFace
          ? faceValue(res, rows.find((r) => r && r.name === 'cards'),
            rows.find((r) => r && r.name === 'text'))
          : (res ? res.key : undefined);
        const w = iframeRef.current && iframeRef.current.contentWindow;
        if (w) {
          try {
            w.postMessage({
              type: HOST.INVOKE_RESULT, reqId,
              ok: !err, value,
            }, '*');
          } catch (e) { /* frame gone */ }
        }
      }
      // card-sandbox-messaging-push: MAIL is intentionally NOT handled here
      // anymore. The helper's reply/push arrives on the CHAT socket (the invoke
      // stamps aveq_id = chat threadId) and is routed to the matching card BY KIND
      // via Redux (meta.cardPush → the relay effect below). Relaying MAIL off this
      // last-subscriber socket was unfiltered and leaked other participants'
      // messages on a shared station — the boundary this slug closes.
    };
    return () => {
      try { socket.close(); } catch (e) { /* already gone */ }
      socketRef.current = null;
      dedSessionRef.current = null;
      // A tile card is torn down on every refresh; leaving fallback timers behind would fire
      // a refresh nudge for an instance that no longer exists.
      Object.values(pendingRef.current).forEach((p2) => p2 && p2.timer && clearTimeout(p2.timer));
      pendingRef.current = {};
    };
  }, [doc, stationId, needsChannel]);

  // card-sandbox-messaging-push: relay a kind-targeted push (routed here via Redux
  // from the CHAT socket — meta.cardPush/cardKind → setCardPush) to THIS card's
  // onPush, ONLY when the push's kind matches this card. The kind guard IS the fix:
  // it stops a shared-room card (or a second card on the station) from receiving
  // another card's messages — the unfiltered-relay leak this slug closes.
  // `cardPush.seq` gives each dispatch a fresh identity so an identical repeat fires.
  React.useEffect(() => {
    if (!doc || !cardPush) return;
    // A push for a live event goes to that one card; a push for none goes to its kind.
    if (cardPush.liveEvent ? cardPush.liveEvent !== liveEventId : cardPush.kind !== kind) return;
    const w = iframeRef.current && iframeRef.current.contentWindow;
    if (w) {
      try { w.postMessage({ type: HOST.PUSH, message: cardPush.message }, '*'); }
      catch (e) { /* frame gone */ }
    }
  }, [cardPush, doc, kind, liveEventId]);

  // Bridge messages from the card's iframe. Everything trust-relevant is delegated
  // to handleCardMessage; here we only gate on source identity and wire callbacks.
  React.useEffect(() => {
    if (!doc) return undefined;
    function onMessage(event) {
      const iframeWindow = iframeRef.current && iframeRef.current.contentWindow;
      if (!isFromCard(event, iframeWindow)) return;   // not our card's frame
      const send = (m) => { try { iframeWindow.postMessage(m, '*'); } catch (e) { /* frame gone */ } };
      handleCardMessage(event.data, {
        send,
        actionGate: gate,
        carriedContinue: sandboxContinue(kind, structured),
        onAction: (action, s) => onAction(action, s || structured),
        // v2 host-mediated backend call. The gate already vetted the action; here we
        // stamp identity from the chat session (station/thread — never from the card),
        // send it on the DEDICATED qlink socket, and settle the card's reqId with the
        // ref-correlated PAYLOAD reply (or {ok:false} on no session / timeout).
        onInvoke: async (reqId, action, cardStructured) => {
          if (!(entry && entry.action_url)) {
            await whenReady(() => {
              const s = sessionRef.current.station;
              const sock = socketRef.current;
              return !!(s && s.station && sock && sock.readyState === 1 && dedSessionRef.current);
            });
          }
          const { station: st, threadId: chatTid } = sessionRef.current;
          const tid = isFace ? null : chatTid;
          const socket = socketRef.current;
          const sessionId = dedSessionRef.current;
          const postToCard = (m) => {
            const w = iframeRef.current && iframeRef.current.contentWindow;
            if (w) { try { w.postMessage(m, '*'); } catch (e) { /* frame gone */ } }
          };
          // standalone-card-action: AGENTLESS transport. If the card's package was
          // served with an `action_url` (the app's own standalone worker), POST the
          // action there and settle the card's reqId with the worker's REAL reply —
          // no aveq-agent, no qbric hop. The gate already vetted the action; the
          // worker re-checks its own fail-closed gate + server-mints the identity.
          if (entry && entry.action_url) {
            const stId = st && st.station && st.station.station_id;
            if (!stId) { postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false }); return; }
            const wireRef = `cardaction_${kind}:${reqId}`;
            _actionFetch(entry.action_url, invokeParams(action, stId, tid, wireRef))
              .then((reply) => postToCard({
                type: HOST.INVOKE_RESULT, reqId,
                ok: !!(reply && reply.ok), value: reply ? reply.result : undefined,
              }))
              .catch(() => postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false }));
            return;
          }
          if (!st || !st.station || !socket || socket.readyState !== 1 /* OPEN */ || !sessionId) {
            postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false });
            return;
          }
          // A unique wire ref for the qlink invoke (fire-and-forget — the reply
          // returns asynchronously as a MAIL, not correlated by this ref).
          const wireRef = `cardinvoke_${kind}:${refSaltRef.current}:${reqId}`;
          const params = isFace
            ? faceInvokeParams(action, structured, wireRef)
            : invokeParams(action, st.station.station_id, tid, wireRef, liveEventId);
          if (!params) { postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false }); return; }
          if (onActivity) onActivity();
          try {
            socket.send(JSON.stringify({
              messageType: 'ACTION',
              actionUri: 'qlink:invoke',
              sessionId,
              params,
            }));
          } catch (e) {
            postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false });
            return;
          }
          // The refresh nudge: an embed HOST page re-fetches its re-invoke render, which
          // REBUILDS the iframe and remounts the card. It carries the card's `resume_token` so
          // the re-invoke RESUMES the playthrough rather than building a new one. No listener
          // outside embed pages → inert in chat.
          //
          // Since !636 this is no longer a tile's state channel — only its FALLBACK, for when
          // the rail does not answer. Anything that reads as "the tile refreshes after every
          // click" describes the old behaviour.
          const nudge = () => {
            const resume = (cardStructured && cardStructured.resume_token) || undefined;
            try {
              window.dispatchEvent(new CustomEvent('aveq:card-invoked',
                { detail: { kind, resume } }));
            } catch (e) { /* CustomEvent unavailable — no refresh nudge */ }
          };
          // A TILE invoke waits for the rail's real reply instead of acking blind. With no
          // chat thread the helper's push has no address, so the refresh used to be the only
          // state channel — and it REBUILDS the iframe, destroying the card instance on every
          // interaction. Correlating the reply keeps the card alive, which is what chat has
          // always had.
          if (!tid) {
            pendingRef.current[wireRef] = {
              reqId,
              // Fallback, deliberately visible in the code rather than implicit: if the rail
              // does not answer (an older agent, a dropped socket), ack the send and fall back
              // to the refresh channel, which is exactly the previous behaviour.
              timer: setTimeout(() => {
                delete pendingRef.current[wireRef];
                // `ok: false`, NOT an empty success. `{ok: true, value: undefined}` matches
                // neither branch of a card's usual handler, so the card sits in `busy` — and
                // recovered only because the nudge below rebuilt the iframe and threw the
                // stuck state away, i.e. by leaning on the very remount this change exists to
                // stop relying on. Parity with the old immediate ack is not the right goal:
                // that ack was immediate, this arrives after eight seconds of waiting, which
                // is a different thing to tell a card. (!636 #2.)
                postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: false });
                nudge();
              }, TILE_REPLY_TIMEOUT_MS),
            };
            return;
          }
          // Chat: the helper's reply arrives asynchronously as a MAIL relayed to the card via
          // host:push, NOT a ref-correlated PAYLOAD — so ack the send now. Unchanged.
          postToCard({ type: HOST.INVOKE_RESULT, reqId, ok: true });
          nudge();
        },
        onResize: (h) => { if (!fillHeight) setHeight(Math.max(40, Math.ceil(h))); },
        // A card that starts moving on its own is invisible if the chat has scrolled past it.
        // `block: 'nearest'` scrolls only when it is actually out of view, so a card already
        // on screen is left exactly where it is. Rate-limited because the card is untrusted:
        // a reveal per frame would fight the reader for control of the page.
        onReveal: () => {
          const now = Date.now();
          if (now - lastRevealRef.current < 2000) return;
          lastRevealRef.current = now;
          // The card usually lives in the OUTPUT DRAWER, which overlays the chat and is
          // shut by default. That drawer opens when `events` changes — but a run updates the
          // card through host:push, which never touches `events`, so it played on behind a
          // closed drawer. Scrolling a hidden drawer into view achieves nothing, which is
          // why revealing appeared to do nothing at all. Announce it; ChatInterface owns the
          // drawer and decides. (Same shape as the aveq:card-invoked nudge.)
          // Carry WHICH card is asking. The drawer is single today so nothing reads it, but
          // "the host decides" is only true if the host can tell them apart — and with two
          // cards on a page, A's reveal must not open the drawer onto B. Adding the field
          // later would change the message shape, which is the expensive kind of change.
          try { window.dispatchEvent(new CustomEvent('aveq:card-reveal', { detail: { kind } })); }
          catch (e) { /* no CustomEvent — the scroll below still applies */ }
          const el = iframeRef.current;
          if (el && el.scrollIntoView) {
            try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
            catch (e) { /* older engines: no options form */ }
          }
        },
        // card-pager-swipe: forward an in-card horizontal swipe to the host pager (if any).
        onSwipe: (dir) => { if (onSwipe) onSwipe(dir); },
        onEnd: () => { if (onEnd) onEnd(); },
        storage: store,
        onReady: (proto) => {
          if (!protocolCompatible(proto)) { setFailed(true); return; }
          // resync (v2): tell a stateful card it may request current state via invoke.
          send({ type: HOST.INIT, protocol: proto, kind, structured, resync: proto >= 2,
                 reduceMotion: Boolean(reduceMotion) });
          if (onLoaded) onLoaded();
          // station-offline-pause: if the chat WS is already down at load, start the
          // card suspended (default is running); transitions are relayed by the effect.
          if (!onlineRef.current) send({ type: HOST.LIFECYCLE, event: 'suspend' });
          if (hiddenRef.current) send({ type: HOST.LIFECYCLE, event: 'hide' });
        },
        log: (reason, detail) => {
          // eslint-disable-next-line no-console
          if (process.env.NODE_ENV !== 'production') console.warn('[card-sandbox]', reason, detail);
        },
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [doc, gate, store, kind, structured, onAction, fillHeight, onSwipe, reduceMotion, onLoaded,
      liveEventId, onEnd, onActivity]);

  // station-offline-pause: relay chat-WS up/down to the card as suspend/resume so a
  // live card (e.g. Suika) can pause while offline and start a fresh session on
  // reconnect. Initial state is handled in onReady; here we fire only on transitions
  // (the card is ready + listening by then).
  const prevOnlineRef = React.useRef(online);
  React.useEffect(() => {
    const prev = prevOnlineRef.current;
    prevOnlineRef.current = online;
    if (prev === online) return;
    const w = iframeRef.current && iframeRef.current.contentWindow;
    if (w) { try { w.postMessage({ type: HOST.LIFECYCLE, event: online ? 'resume' : 'suspend' }, '*'); } catch (e) { /* frame gone */ } }
  }, [online]);

  // Not 'suspend': a card may restart on 'resume' (Suika does), and coming back into view is
  // not a reconnect.
  const prevHiddenRef = React.useRef(hidden);
  React.useEffect(() => {
    const prev = prevHiddenRef.current;
    prevHiddenRef.current = hidden;
    if (!!prev === !!hidden) return;
    const w = iframeRef.current && iframeRef.current.contentWindow;
    if (w) { try { w.postMessage({ type: HOST.LIFECYCLE, event: hidden ? 'hide' : 'show' }, '*'); } catch (e) { /* frame gone */ } }
  }, [hidden]);

  // A caller with its own fallback (a face's frame) gets told instead of a generic card.
  if (failed) return onFailed ? null : <GenericCard structured={structured} onAction={onAction} />;
  if (!doc) return <GenericCard structured={structured} onAction={onAction} />;

  return (
    <iframe
      ref={iframeRef}
      title={`card-${kind}`}
      // No allow-same-origin → opaque origin: no cookies, no parent DOM/storage.
      // allow-scripts is the only capability. No `allow` features granted.
      sandbox="allow-scripts"
      srcDoc={doc}
      style={{
        width: '100%', height: fillHeight ? '100%' : height, border: 'none', display: 'block',
        borderRadius: '8px', background: 'transparent', overflow: 'hidden',
      }}
    />
  );
}
